import { ApiError } from '@ghp/api-client'
import { loginRequestSchema, type LoginRequest, type TeamChoice } from '@ghp/domain'

import { SessionWriteError } from './session'

/**
 * Ce qu'une connexion ratée laisse à l'écran, et ce que l'écran en fait.
 *
 * Deux chemins mènent ici : l'écran de connexion (`routes/login.tsx`) et le panneau de connexion
 * du rail (`components/ConnectionPanel.tsx`), qui rouvre une session expirée **sans faire sortir
 * l'agent de sa tournée**. Les deux affichent les mêmes phrases parce que ce sont les mêmes
 * échecs : les dédoubler les aurait laissées diverger, et un agent aurait lu deux diagnostics
 * différents de la même panne selon la porte qu'il avait poussée.
 */

const LOGIN_FIELDS = ['login', 'password'] as const

type LoginField = (typeof LOGIN_FIELDS)[number]

/**
 * Ce que l'écran retient d'un échec : le message pour l'agent, et **quels champs** sont en cause.
 *
 * ⚠️ Les deux ne se déduisent pas l'un de l'autre : seuls un refus et un champ vide mettent la
 * saisie en cause, et peindre en rouge des champs justes ferait retaper ses identifiants à l'agent
 * au lieu de regarder son réseau ou son stockage.
 */
export interface LoginFailure {
  message: string
  faulty: readonly LoginField[]
}

/**
 * Ce que l'agent doit comprendre, pas ce que le réseau a dit : un refus (401) n'est pas
 * une limite de tentatives (429, D3), qui n'est pas une absence de réseau — sur le
 * terrain, la troisième est la plus fréquente et n'est pas une faute de l'agent.
 *
 * Le quatrième cas ne vient pas du réseau du tout : le serveur a authentifié l'agent et la
 * tablette n'a pas su écrire la session. Le dire ferme la fausse piste (« vérifier le
 * réseau ») et nomme la seule action utile — libérer du stockage.
 */
export function failureFor(thrown: unknown): LoginFailure {
  if (thrown instanceof SessionWriteError) {
    return {
      message:
        'Identifiants acceptés, mais la tablette n’a pas pu enregistrer la session — libérer de l’espace de stockage, puis réessayer.',
      faulty: [],
    }
  }
  if (thrown instanceof ApiError) {
    if (thrown.status === 401) {
      // Les deux : le serveur ne dit pas lequel, sans quoi il énumérerait les comptes.
      return { message: 'Identifiant ou mot de passe refusé.', faulty: LOGIN_FIELDS }
    }
    if (thrown.status === 429) {
      return {
        message: 'Trop de tentatives — patienter quelques minutes avant de réessayer.',
        faulty: [],
      }
    }
    return {
      message: `Le serveur a répondu une erreur (${thrown.status}). Réessayer, puis prévenir le bureau.`,
      faulty: [],
    }
  }
  return { message: 'Serveur injoignable — vérifier le réseau et réessayer.', faulty: [] }
}

/**
 * La saisie incomplète, arrêtée **avant** le réseau : `/auth/login` n'accepte que cinq tentatives
 * par minute ([D3](../../../../../docs/ARCHITECTURE.md#d3)). ⚠️ La règle est `loginRequestSchema`,
 * pas une réécriture : une forme de `login` inventée ici finirait par refuser un compte valide.
 */
export function incompleteEntry(
  credentials: Pick<LoginRequest, 'login' | 'password'>,
): LoginFailure | null {
  const parsed = loginRequestSchema.safeParse(credentials)
  if (parsed.success) return null

  const faulty = LOGIN_FIELDS.filter((field) =>
    parsed.error.issues.some((issue) => issue.path[0] === field),
  )

  // Un manquement qu'aucun champ ne porte part au serveur : personne ne saurait le corriger ici.
  if (faulty.length === 0) return null

  if (faulty.length > 1) return { message: 'Identifiant et mot de passe obligatoires.', faulty }

  return {
    message: faulty[0] === 'password' ? 'Mot de passe obligatoire.' : 'Identifiant obligatoire.',
    faulty,
  }
}

/**
 * Marqué invalide **et** décrit par le message : `role="alert"` ne l'annonce qu'une fois, et un
 * lecteur d'écran revenant au champ entendrait « invalide » sans savoir pourquoi.
 */
export function faultOf(failure: LoginFailure | null, field: LoginField, failureId: string) {
  const atFault = failure?.faulty.includes(field) ?? false

  return { invalid: atFault, 'aria-describedby': atFault ? failureId : undefined }
}

/**
 * Ce que porte le bouton d'une équipe : son nom, **et de quoi la reconnaître quand une autre
 * porte le même**.
 *
 * ⚠️ **Deux équipes homonymes ne sont pas une anomalie** : `Team.name` se dérive des initiales de
 * ses deux agents (M22), donc une paire recomposée — après une dissolution, par exemple — donne
 * deux fois `PD·LM`. L'agent se retrouvait alors devant deux boutons identiques et choisissait à
 * pile ou face la tournée de sa journée (#176).
 *
 * ⚠️ **Le repère ne s'affiche que sur les homonymes.** Coller son identifiant de tablette sous
 * chaque équipe ferait lire un jeton technique à qui n'a qu'à reconnaître son binôme ; ici il ne
 * paraît qu'au moment où le nom ne suffit plus.
 *
 * ⚠️ Reste le cas d'une équipe **sans tablette rattachée** (0..1, M22) : on le dit, plutôt que de
 * numéroter les options. Un « 1 » et un « 2 » ne désigneraient rien que l'agent puisse vérifier —
 * et deux équipes homonymes sans tablette resteraient, elles, indiscernables.
 */
export function teamLabel(team: TeamChoice, teams: readonly TeamChoice[]): string {
  const homonym = teams.some((other) => other.id !== team.id && other.name === team.name)
  if (!homonym) return team.name

  return `${team.name} · ${team.accountLogin ?? 'sans tablette'}`
}
